// @flow

import * as React from 'react';
import { Flex, Box, useSystem } from 'react-system';
import Divider from '@material-ui/core/Divider';
import { ButtonBase } from '@material-ui/core';
import { graphql, useFragment } from 'react-relay';
import { formatDate } from '../../controls/date-input.js';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { InlineMarked } from '../../controls/marked.js';

import { ExpandMore } from '../../icons/expand-more';

import type { AggregatesListingEnergyDiagnostics_listing$key } from './__generated__/AggregatesListingEnergyDiagnostics_listing.graphql.js';

type Props = {|
  listing: AggregatesListingEnergyDiagnostics_listing$key,
|};

const Stack = ({ children }: {| children: React.Node |}) => {
  const { media } = useSystem();
  return (
    <div
      css={media({
        display: 'grid',
        gap: 24,
        gridTemplateColumns: ['1fr', '50% 50%'],
      })}
    >
      {children}
    </div>
  );
};

const useEnergyRate = () => {
  const { t } = useLocale();
  return [
    { class: 'a', range: '< 50', color: '#569361' },
    {
      class: 'b',
      range: t('range', {
        from: 52,
        to: 90,
      }),
      color: '#8ABD86',
    },
    {
      class: 'c',
      range: t('range', {
        from: 91,
        to: 150,
      }),
      color: '#DFD46B',
    },
    {
      class: 'd',
      range: t('range', {
        from: 151,
        to: 230,
      }),
      color: '#DCB75A',
    },
    {
      class: 'e',
      range: t('range', {
        from: 231,
        to: 330,
      }),
      color: '#E0943B',
    },
    {
      class: 'f',
      range: t('range', {
        from: 331,
        to: 450,
      }),
      color: '#DD6565',
    },
    { class: 'g', range: '> 450', color: '#C9454E' },
  ];
};
const useGasRate = () => {
  const { t } = useLocale();

  return [
    { class: 'a', range: '< 5', color: '#F6EDFE' },
    {
      class: 'b',
      range: t('range', {
        from: 6,
        to: 10,
      }),
      color: '#E4C7FB',
    },
    {
      class: 'c',
      range: t('range', {
        from: 11,
        to: 20,
      }),
      color: '#D2ADF1',
    },
    {
      class: 'd',
      range: t('range', {
        from: 21,
        to: 35,
      }),
      color: '#C99AEF',
    },
    {
      class: 'e',
      range: t('range', {
        from: 36,
        to: 55,
      }),
      color: '#B77AE9',
    },
    {
      class: 'f',
      range: t('range', {
        from: 56,
        to: 80,
      }),
      color: '#A659E9',
    },
    { class: 'g', range: '> 80', color: '#8835D9' },
  ];
};

const LinksButton = ({ onClick, expanded }) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  return (
    <ButtonBase
      onClick={onClick}
      css={{
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '20px',
        color: colors.primaryMain,
      }}
    >
      {expanded ? t('showLess2') : t('showDetails')}
      <ExpandMore
        size={18}
        css={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform .2s',
        }}
      />
    </ButtonBase>
  );
};

const RowDiagram = ({ type = 'energy', value }) => {
  const { colors, text } = useTheme();
  const { t } = useLocale();
  const energyRate = useEnergyRate();
  const gasRate = useGasRate();

  return (
    <Box>
      <p css={[text.subtitle1, { marginBottom: '25px' }]}>
        {type === 'energy'
          ? t('energyConsumption')
          : t('greenhouseGasEmmissions')}
      </p>
      {value == null ? (
        t('noData')
      ) : (
        <Flex
          css={{
            color: colors.white,
            fontWeight: 'bold',
            fontSize: '16px',
            lineHeight: '100%',
            marginBottom: value.value != null ? '4rem' : '1rem',
            '> div:first-of-type': {
              borderRadius: '25% 0 0 25%',
              color: type !== 'energy' ? '#656565' : colors.white,
            },
            '> div:last-child': {
              borderRadius: '0 25% 25% 0',
            },
          }}
        >
          {(type === 'energy' ? energyRate : gasRate).map((rate, index) => (
            <div
              key={index}
              css={{
                width: '32px',
                height: '32px',
                padding: '8px 11px',
                backgroundColor: rate.color,
                position: rate.class === value.class ? 'relative' : 'inherit',
              }}
            >
              <>
                {rate.class.toUpperCase()}
                {rate.class === value.class && (
                  <div
                    css={{
                      position: 'absolute',
                      fontSize: '18px',
                      top: '-6px',
                      left: '-6px',
                    }}
                  >
                    <div
                      css={{
                        width: '44px',
                        height: '44px',
                        padding: '10px 13px',
                        backgroundColor: rate.color,
                        border: '3px solid #FFFFFF',
                        borderRadius: '50%',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'center',
                      }}
                    >
                      {rate.class.toUpperCase()}
                    </div>
                    {value.value != null && (
                      <>
                        <p
                          css={{
                            paddingLeft: '16px',
                            color: colors.black,
                            marginBottom: '4px',
                          }}
                        >
                          {value.value}
                        </p>
                        <p
                          css={{
                            paddingLeft: '16px',
                            color: colors.black,
                            fontWeight: 'normal',
                            fontSize: '14px',
                          }}
                        >
                          <InlineMarked>
                            {type === 'energy'
                              ? t('coeffEnergy')
                              : t('coeffGas')}
                          </InlineMarked>
                        </p>
                      </>
                    )}
                  </div>
                )}
              </>
            </div>
          ))}
        </Flex>
      )}
    </Box>
  );
};

const Diagram = ({ type = 'energy', value }) => {
  const { colors, text } = useTheme();
  const { media } = useSystem();
  const { t } = useLocale();
  const energyRate = useEnergyRate();
  const gasRate = useGasRate();

  return (
    <Box css={{ marginBottom: '16px' }}>
      <p css={[text.subtitle1, { marginBottom: '8px' }]}>
        {type === 'energy'
          ? t('lowEnergyConsumption')
          : t('lowGreenhouseGasEmmissions')}
      </p>
      <Flex
        flexDirection="column"
        css={{
          gap: '4px',
          color: colors.white,
          fontWeight: 'bold',
          fontSize: '16px',
          lineHeight: '100%',
          marginBottom: '8px',
        }}
      >
        {(type === 'energy' ? energyRate : gasRate).map((rate, index) => (
          <Flex
            key={index}
            justifyContent="space-between"
            css={{
              height: '34px',
              ':first-of-type': {
                color: type !== 'energy' ? '#656565' : colors.white,
              },
            }}
          >
            <div
              css={[
                media({
                  width: [
                    `calc(75px + ${index * 17}px)`,
                    `calc(95px + ${index * 17}px)`,
                  ],
                }),
                {
                  position: 'relative',
                  padding: '8px 0 8px 11px',
                  backgroundColor: rate.color,
                  textAlign: 'end',
                  height: '100%',
                  ':after': {
                    content: '""',
                    width: 0,
                    height: 0,
                    borderTop: '17px solid transparent',
                    borderBottom: '17px solid transparent',
                    borderLeft: `17px solid ${rate.color}`,
                    position: 'absolute',
                    left: '100%',
                    top: '0',
                  },
                },
              ]}
            >
              <Flex justifyContent="space-between">
                <p>{rate.range}</p>
                <p>{rate.class.toUpperCase()}</p>
                {rate.class === value.class && (
                  <div
                    css={{
                      fontSize: '18px',
                      width: '44px',
                      height: '44px',
                      position: 'absolute',
                      right: '-22px',
                      top: '-6px',
                      padding: '10px 13px',
                      backgroundColor: rate.color,
                      border: '3px solid #FFFFFF',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'end',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    {rate.class.toUpperCase()}
                  </div>
                )}
              </Flex>
            </div>
            {value.value != null && rate.class === value.class && (
              <>
                <div
                  css={{
                    height: '50%',
                    borderBottom: `1px solid ${colors.black}`,
                    margin: '0 10px 0 20px',
                    flexGrow: 1,
                  }}
                />
                <div css={{ color: colors.black }}>
                  <p css={{ marginBottom: '4px' }}>{value.value}</p>
                  <p css={{ fontWeight: 'normal', fontSize: '14px' }}>
                    <InlineMarked>
                      {type === 'energy' ? t('coeffEnergy') : t('coeffGas')}
                    </InlineMarked>
                  </p>
                </div>
              </>
            )}
          </Flex>
        ))}
      </Flex>
      <p css={text.subtitle1}>
        {type === 'energy'
          ? t('highEnergyConsumption')
          : t('highGreenhouseGasEmmissions')}
      </p>
    </Box>
  );
};

export const AggregatesListingEnergyDiagnostics = (
  props: Props,
): React.Node => {
  const { text } = useTheme();
  const { media } = useSystem();
  const { t, numberLocale, currency: fallbackCurrency } = useLocale();
  const [expanded, toggleExpand] = React.useReducer(show => !show, false);

  const listing = useFragment(
    graphql`
      fragment AggregatesListingEnergyDiagnostics_listing on AggregatesListing {
        energyClass
        greenhouseGasEmissionsClass
        energyIndex
        greenhouseGasEmissionsIndex
        energyCertificateVersion
        energyInspectionDate
        energyReferenceYear
        energyCostCurrency
        estimatedLowerEnergyCost
        estimatedHigherEnergyCost
      }
    `,
    props.listing,
  );

  const currency = listing.energyCostCurrency;

  const date =
    listing.energyInspectionDate != null
      ? new Date(listing.energyInspectionDate)
      : null;

  const inspectionDate = date != null ? formatDate(date) : null;

  const formatCurrency = val =>
    numberLocale.format(val, {
      style: 'currency',
      currency: currency ?? fallbackCurrency,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

  let estimatedCostRange = null;

  if (listing.estimatedLowerEnergyCost != null) {
    estimatedCostRange = formatCurrency(listing.estimatedLowerEnergyCost);
  }

  if (listing.estimatedHigherEnergyCost != null) {
    if (estimatedCostRange != null) {
      estimatedCostRange += ` - ${formatCurrency(
        listing.estimatedHigherEnergyCost,
      )}`;
    } else {
      estimatedCostRange = formatCurrency(listing.estimatedHigherEnergyCost);
    }
  }

  const otherEnergyFields = [
    { label: t('estimatedAnnualCosts'), value: estimatedCostRange },
    { label: t('certificateVersion'), value: listing.energyCertificateVersion },
    { label: t('estimateReferenceYear'), value: listing.energyReferenceYear },
    { label: t('inspectionDate'), value: inspectionDate },
  ].map(item =>
    item.value == null ? { label: item.label, value: t('noData') } : item,
  );

  return (
    <Box pr={[0, 2]} mb={4}>
      <Box as="h2" css={(media([text.h6, text.h5]), { marginBottom: '32px' })}>
        {t('energyDiagnostics')}
      </Box>
      <Stack>
        <RowDiagram
          value={
            listing.energyClass == null
              ? null
              : { class: listing.energyClass, value: listing.energyIndex }
          }
        />
        <RowDiagram
          type="gas"
          value={
            listing.greenhouseGasEmissionsClass == null
              ? null
              : {
                  class: listing.greenhouseGasEmissionsClass,
                  value: listing.greenhouseGasEmissionsIndex,
                }
          }
        />
      </Stack>
      {expanded && (
        <>
          <Stack>
            {listing.energyClass != null && (
              <Diagram
                value={{
                  class: listing.energyClass,
                  value: listing.energyIndex,
                }}
              />
            )}
            {listing.greenhouseGasEmissionsClass != null && (
              <Diagram
                type="gas"
                value={{
                  class: listing.greenhouseGasEmissionsClass,
                  value: listing.greenhouseGasEmissionsIndex,
                }}
              />
            )}
          </Stack>
          <Flex mx={-2} flexWrap="wrap" mb={4} justifyContent="space-between">
            {otherEnergyFields.map(field => (
              <Box width={[1, 4 / 9]} mx={2} key={field.label}>
                <Flex
                  py={[2, 2, 3]}
                  justifyContent="space-between"
                  flexDirection="row"
                  flexWrap="nowrap"
                >
                  <Box flexGrow={1} css={[text.subtitle1, { fontSize: 15 }]}>
                    {field.label}
                  </Box>
                  <Box
                    flexShrink={0}
                    css={[text.body1, { textAlign: 'right', fontSize: 15 }]}
                  >
                    {field.value}
                  </Box>
                </Flex>
                <Divider />
              </Box>
            ))}
          </Flex>
        </>
      )}
      {(listing.greenhouseGasEmissionsClass || listing.energyClass) && (
        <LinksButton onClick={toggleExpand} expanded={expanded} />
      )}
    </Box>
  );
};
